
import {first, map} from 'rxjs/operators';
import { Injectable, Injector } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { NewCasingPlanComponent } from "../new-casing-plan.component";
import { CasingType, CasingTypeModel, DrillingEquipmentModel, FormationModel, CasingPlanComponent } from "../models/casing-plan.model";
import { toCamel } from "../models/casing-plan-functions.model";
import { CASING_PLAN_TOKEN, Alert, DefinedHorizontalWellAlert, UICAlert, DrivePipeLieuConductorAlert, NewWellsAlert, EORAlert, IntermediateStringAlert, ProductionStringAlert, OpenHoleAlert, ClintonPinchoutAlert, TrempealeauAlert, NonMappableUSDWAlert, ShallowSurfaceCasingAlert, ActiveUndergroundMinesAlert, UndergroundActiveMines1000Alert, AbandonedUndergroundMinesAlert, UndergroundAbandonedMines1000Alert, GasStorageFieldAlert, DriftThicknessAlert, MappableUndergroundWaterAlert, NonMappableUndergroundWaterAlert, UndergroundInjectionControlAORAlert, HydrogenSulfideAlert, ThinTillAlert, LakePlainAlert, MeccaPoolAlert, KarstAreaAlert, SolutionMiningAreaAlert, MunicipalProtectionAreaAlert, WaterWellsNonMappableUSDWAlert, PerennialStreamsNonMappableUSDWAlert, AirRotaryToolInUrbanAreaAlert } from "../models/Alerts.model";
import { DomainDefinitionService } from "../../../../builder/services/domain-definition.service";
import { WellType } from '../../../../common/models/well.model';


@Injectable()
export class CasingConstructionRulesService {

    alerts: Alert[] = [];
    alerts$ = new BehaviorSubject<Alert[]>([]);
    availableCasings$ = new BehaviorSubject<CasingTypeModel[]>([]);
    availableCasings: CasingTypeModel[] = [];
    availableFormations$ = new BehaviorSubject<FormationModel[]>([]);
    availableFormations: FormationModel[] = [];
    availableTools$ = new BehaviorSubject<DrillingEquipmentModel[]>([]);
    availableTools: DrillingEquipmentModel[] = [];    
    casingPlanInjector: Injector;
    completionEligible$ = new BehaviorSubject<string>("no");
    component: CasingPlanComponent;
    observableAlerts: Alert[] = [];
    observableAlerts$ = new BehaviorSubject<Alert>(null);

    defaultCasing = { code: "-1", definition: CasingType.Default, isActive: true, isRequired: false, lastModifiedDateUTC: "" } as CasingTypeModel;
    defaultFormation = { code: "-1", definition: "", isActive: true, isRequired: true, lastModifiedDateUTC: "" } as FormationModel;
    defaultTool = { code: "-1", definition: " ", isActive: true, isRequired: true, lastModifiedDateUTC: "" } as DrillingEquipmentModel;
    

    checkForApplicableAlerts(classes: any[], alerts: Alert[] = this.observableAlerts): boolean {
        let required: boolean = false;

        for (let o = 0; o < alerts.length; o++) {
            const alert = alerts[o];
            for (let x = 0; x < classes.length; x++) {
                if (alert instanceof classes[x]) {
                    required = alert.applicable;
                }
            }
            if (required) break;
        }

        return required;
    }

    get conductorRequired(): boolean {
        return this.checkForApplicableAlerts([NonMappableUSDWAlert, ShallowSurfaceCasingAlert, MunicipalProtectionAreaAlert, ActiveUndergroundMinesAlert]);
    }

    get intermediateRequired(): boolean {
        let required: boolean = false;
        required = this.checkForApplicableAlerts([DefinedHorizontalWellAlert], this.alerts)
                     || this.checkForApplicableAlerts([ClintonPinchoutAlert, GasStorageFieldAlert]);
        return required;
    }

    get packerRequired(): boolean {
        return this.checkForApplicableAlerts([EORAlert], this.alerts);
    }  

    get withinAbandonedMineArea(): boolean {
        return this.checkForApplicableAlerts([AbandonedUndergroundMinesAlert]);
    }

    get withinAbandonedMineBufferArea(): boolean {
        return this.checkForApplicableAlerts([UndergroundAbandonedMines1000Alert]);
    }

    get withinActiveMineArea(): boolean {
        return this.checkForApplicableAlerts([ActiveUndergroundMinesAlert]);
    }

    get withinActiveMineBufferArea(): boolean {
        return this.checkForApplicableAlerts([UndergroundActiveMines1000Alert]);
    }

    get withinMineArea(): boolean {
        return this.checkForApplicableAlerts([ActiveUndergroundMinesAlert, AbandonedUndergroundMinesAlert]);
    }

    get withinMineBufferArea(): boolean {
        return this.checkForApplicableAlerts([UndergroundActiveMines1000Alert, UndergroundAbandonedMines1000Alert]);
    }

    get restrictOnType(): boolean {
        let result: boolean = false;
        if (this.component && this.component.wellType) {
            const typeRestrictions = [{ description: "EORP", type: "ER_R" }, { description: "", type: "SW_R" }, { description: "", type: "SM_R" }] as WellType[];
            result = typeRestrictions.findIndex(t => this.component.wellType === t.type && this.component.wellTypeDescription === t.description) !== -1;
        }
        return result;
    }

    constructor(public readonly domainService: DomainDefinitionService, protected injector: Injector) {
        
    }

    evaluateAlerts(): void {
        this.alerts$.next([]);
        for (let a = 0; a < this.alerts.length; a++) {
            this.alerts[a].validate();
        }        
        this.alerts$.next(this.alerts);
        for (let a = 0; a < this.observableAlerts.length; a++) {
            (<Observable<boolean>>this.observableAlerts[a].validate()).pipe(first()).subscribe((result) => {
                this.observableAlerts$.next(this.observableAlerts[a]);
            });
        }
    }

    getAllowableStrings(previousCasingType: string): Observable<CasingTypeModel[]> {
        return this.domainService.getValues("CSG_STRING").pipe(strings => {
            return strings.pipe(map(strings => {                
                if (!this.availableCasings || this.availableCasings.length === 0) {
                    this.availableCasings = [];            
                    this.availableCasings.push(this.defaultCasing);
                    for (let s = 0; s < strings.length; s++) {                        
                        let model = new CasingTypeModel();
                        model.code = strings[s].Code;
                        model.definition = this.parseCasingDefinition(strings[s].Definition),
                        model.isActive = strings[s].IsActive;
                        model.isRequired = strings[s].IsRequired;
                        model.lastModifiedDateUTC = strings[s].LastModifiedDateUTC;
                        model.tags = strings[s].Tags;
                        this.availableCasings.push(model);                        
                    }
                    this.availableCasings$.next(this.availableCasings);
                }
                return this.applyCasingFilter(previousCasingType, this.availableCasings);                
            }));
        });
    }

    getAllowableTools(casingType: string, isUrban: boolean): Observable<DrillingEquipmentModel[]> {
        return this.domainService.getValues("RIG_TYP").pipe(tools => {
            return tools.pipe(map(tools => {               
                if (!this.availableTools || this.availableTools.length === 0) {
                    this.availableTools = [];  
                    this.availableTools.push(this.defaultTool);
                    for (let t = 0; t < tools.length; t++) {
                        const tool = new DrillingEquipmentModel();
                        tool.code = tools[t].Code;
                        tool.definition = tools[t].Definition;
                        tool.isActive = tools[t].isActive;
                        tool.isRequired = tools[t].isRequired;
                        tool.lastModifiedDateUTC = tools[t].LastModifiedDateUTC;
                        this.availableTools.push(tool);
                    }
                    this.availableTools$.next(this.availableTools);
                }
                return this.applyToolFilter(casingType, isUrban, tools);
            }));
        });
    }

    getAllowableFormations(previousCasingFormation: string): Observable<FormationModel[]> {
        return this.domainService.getValues("FORMATION").pipe(formations => {
            return formations.pipe(map(formations => {
                const data = toCamel(formations);
                if (!this.availableFormations || this.availableFormations.length === 0) {
                    this.availableFormations = data;                    
                    this.availableFormations$.next(this.availableFormations);
                }
                return this.applyFormationFilter(previousCasingFormation, data);
            }));
        });
    }

    getConstructionAlerts(casingPlan: CasingPlanComponent): void {
        this.alerts = [];
        this.component = casingPlan;
        const provider = [{ provide: CASING_PLAN_TOKEN, useValue: casingPlan }];
        this.alerts.push(new NewWellsAlert(Injector.create(provider, this.injector)));     
        this.alerts.push(new EORAlert(Injector.create(provider, this.injector)));
        this.alerts.push(new IntermediateStringAlert(Injector.create(provider, this.injector)));
        this.alerts.push(new ProductionStringAlert(Injector.create(provider, this.injector)));
        this.alerts.push(new OpenHoleAlert(Injector.create(provider, this.injector)));
        this.alerts.push(new DrivePipeLieuConductorAlert(Injector.create(provider, this.injector)));
        this.alerts.push(new DefinedHorizontalWellAlert(Injector.create(provider, this.injector)));
        this.alerts.push(new UICAlert(Injector.create(provider, this.injector)));
        this.alerts.push(new AirRotaryToolInUrbanAreaAlert(Injector.create(provider, this.injector)));
        
        this.alerts$.next(this.alerts);      
        this.observableAlerts.push(new TrempealeauAlert(Injector.create(provider, this.injector)));
        this.observableAlerts.push(new ShallowSurfaceCasingAlert(Injector.create(provider, this.injector)));
        this.observableAlerts.push(new ActiveUndergroundMinesAlert(Injector.create(provider, this.injector)));
        this.observableAlerts.push(new UndergroundActiveMines1000Alert(Injector.create(provider, this.injector)));
        this.observableAlerts.push(new AbandonedUndergroundMinesAlert(Injector.create(provider, this.injector)));
        this.observableAlerts.push(new UndergroundAbandonedMines1000Alert(Injector.create(provider, this.injector)));
        this.observableAlerts.push(new GasStorageFieldAlert(Injector.create(provider, this.injector)));
        this.observableAlerts.push(new DriftThicknessAlert(Injector.create(provider, this.injector)));
        this.observableAlerts.push(new MappableUndergroundWaterAlert(Injector.create(provider, this.injector)));
        this.observableAlerts.push(new UndergroundInjectionControlAORAlert(Injector.create(provider, this.injector)));
        this.observableAlerts.push(new HydrogenSulfideAlert(Injector.create(provider, this.injector)));
        this.observableAlerts.push(new ThinTillAlert(Injector.create(provider, this.injector)));
        this.observableAlerts.push(new LakePlainAlert(Injector.create(provider, this.injector)));
        this.observableAlerts.push(new MeccaPoolAlert(Injector.create(provider, this.injector)));
        this.observableAlerts.push(new KarstAreaAlert(Injector.create(provider, this.injector)));
        this.observableAlerts.push(new SolutionMiningAreaAlert(Injector.create(provider, this.injector)));        
        this.observableAlerts.push(new MunicipalProtectionAreaAlert(Injector.create(provider, this.injector)));
        this.observableAlerts.push(new NonMappableUndergroundWaterAlert(Injector.create(provider, this.injector)));
        this.observableAlerts.push(new WaterWellsNonMappableUSDWAlert(Injector.create(provider, this.injector)));        
        this.observableAlerts.push(new PerennialStreamsNonMappableUSDWAlert(Injector.create(provider, this.injector)));
        this.observableAlerts.push(new NonMappableUSDWAlert(Injector.create(provider, this.injector)));
        this.getAllowableFormations("").subscribe((formations) => {
            this.component.formations = this.availableFormations;
            this.observableAlerts.push(new ClintonPinchoutAlert(Injector.create(provider, this.injector)));
            this.subscribeToAlerts();
        })
        
        
    }

    

    public applyCasingFilter(previousCasingType: string, strings: CasingTypeModel[]): CasingTypeModel[] {
        let available: CasingTypeModel[] = [];
        available.push(this.defaultCasing);
        for (let s = 0; s < strings.length; s++) {           
            let filter: string[] = this.getNextStrings(previousCasingType);            
            if (filter.findIndex(f => f === strings[s].code) !== -1) {
                const model = new CasingTypeModel();
                model.code = strings[s].code;
                model.definition = (strings[s].definition.toLowerCase().indexOf("hole") === -1) ? strings[s].definition : CasingType.OpenHole;
                model.isActive = strings[s].isActive;
                model.isRequired = strings[s].isRequired;
                model.lastModifiedDateUTC = strings[s].lastModifiedDateUTC;
                model.tags = strings[s].tags;
                available.push(model);
            }
        }
        return available;      
    }

    public applyFormationFilter(previousFormation: string, formations: any[]): FormationModel[] {        
        if (previousFormation) {
            const previousIndex = formations.findIndex(f => f.definition === previousFormation);
            if (previousIndex !== -1) {
                formations = formations.slice(previousIndex + 1, formations.length);
            }
        }
        formations.unshift(this.defaultFormation);
        return formations;
    }

    public applyToolFilter(casingType: string, isUrban: boolean, tools: any[]): DrillingEquipmentModel[] {
        let available: DrillingEquipmentModel[] = [];
        available.push(this.defaultTool);
        let filter: string[] = [];
        filter.push("cable");
        filter.push("air rotary");
        filter.push("fluid rotary");
        if (casingType === CasingType.Conductor) {
            filter.push("concentric");
        }
                
        for (let t = 0; t < tools.length; t++) {
            if (filter.findIndex(f => f === tools[t].Definition.toLowerCase()) !== -1) {
                const tool = new DrillingEquipmentModel();
                tool.code = tools[t].Code;
                tool.definition = tools[t].Definition;
                tool.isActive = tools[t].isActive;
                tool.isRequired = tools[t].isRequired;
                tool.lastModifiedDateUTC = tools[t].LastModifiedDateUTC;
                available.push(tool);
            }
        }
        return available;
    }

    public parseCasingDefinition(definition: string): CasingType {
        let type: CasingType;
        if (definition.toLowerCase().indexOf("hole") !== -1) {
            type = CasingType.OpenHole;
        } else if (definition.toLowerCase().indexOf("tubing") !== -1) {
            type = CasingType.Tubing;
        } else {
            type = definition as CasingType;
        }
        return type;
    }

    public subscribeToAlerts(): void {
        for (let a = 0; a < this.observableAlerts.length; a++) {
            (<Observable<boolean>>this.observableAlerts[a].validate()).pipe(first()).subscribe((result) => {
                this.observableAlerts$.next(this.observableAlerts[a]);
            });
        }
    }

    checkCompletionEligibility(currentCasing: string): void {
        let result = "no";
        switch (currentCasing) {
            case "PROD":
            case "HOL1":
            case "L1":
                result = "yes";
                break;
            case "T1":
                result = "required";
                break;
        }
        this.completionEligible$.next(result);
    }

    getNextStrings(previousString: string): string[] {
        let filter: string[] = [];
       
        switch (previousString) {
            case "COND": //conductor
            case "CND*":                    
                filter.push("SURF");
                if (this.withinMineBufferArea) {
                    filter.push("MS1");
                }        
                break;
            case "DP*": //drive pipe               
                filter.push("COND");
                if (!this.conductorRequired) {                    
                    filter.push("SURF");
                } 
                if (this.withinMineBufferArea) {
                    filter.push("MS1");
                }                
                break;
            case "HOL1": //open hole                
                filter = [, "T1", "L1"];
                this.completionEligible$.next("yes");
                break;
            case "I1": //intermediate casing 1
                filter = ["I2", "PROD", "HOL1", "T1", "L1" ];
                break;
            case "I2": //intermediate casing 2
                filter = ["PROD", "HOL1", "T1", "L1"];
                break;
            case "L1": //liner
                filter = ["T1"];
                this.completionEligible$.next("yes");
                break;
            case "MS1": //mine string
                filter.push("MS1");
                filter.push("I1");
                const hasSurface = this.component && this.component.casingPlan.wellSegments.findIndex(s => s.casingType.definition === CasingType.Surface) !== -1;
                if (!hasSurface) {
                    filter.push("SURF");
                }
                if (!this.intermediateRequired) {
                    filter.push("PROD");
                }                
                break;
            case "PROD":  //production casing
                filter = [ "T1", "L1", "HOL1"];
                this.completionEligible$.next("yes");
                break;
            case "SURF": //surface casing
            case "SRF*": 
                const hasMine = this.component && this.component.casingPlan.wellSegments.findIndex(s => s.casingType.definition === CasingType.MineString) !== -1;
                if (!hasMine && this.withinMineArea) {
                    filter.push("MS1");
                } else {
                    filter.push("I1");
                    if (!this.intermediateRequired) {
                        filter.push("PROD");
                        if (!this.restrictOnType) {
                            filter.push("HOL1");
                        }
                    }
                    if (this.withinMineBufferArea) {
                        filter.push("MS1");
                    }
                }                
                break;
            case "T1": //tubing
                filter = [];
                this.completionEligible$.next("required");
                break;
            default: //top hole 
                filter.push("DP*");
                filter.push("COND");
                if (!this.conductorRequired) {
                    filter.push("SURF");
                    if (this.withinMineArea || this.withinMineBufferArea) {
                        filter.push("MS1");
                    }
                }
                else {
                    //If conductor is required, and we're in an abandoned mine area, mine string is an option
                    if (this.withinAbandonedMineBufferArea) {
                        filter.push("MS1");
                    }

                }
                break;
        }
        return filter;
    }

    
}
